<template>
  <q-item-wrapper
    clickable
    :class="{'bg-red-2': Object.keys(errors).length > 0}"
    @click="dialogOpen = true"
  >
    <div class="row q-gutter-sm">
      <div class="flex-100">
        <nl2br
          :class="!row.description ? 'text-bold opaque-40 text-italic' : ''"
          :text="row.description || 'No description provided'"
        />
      </div>

      <focus-field
        disable
        class="col-auto line-quantity"
        caption="Qty / Hrs"
        :label="`${row.quantity}`"
      />

      <focus-field
        disable
        class="col-auto"
        caption="Unit Price"
        :label="`\$${row.unit_price}`"
      />

      <focus-field
        disable
        class="col-auto"
        caption="Discount"
        :label="`${row.discount_rate}%`"
      />

      <focus-field
        disable
        class="col-auto text-blue-7 line-total"
        caption="Line Total"
        :label="linePriceStr"
      />
    </div>

    <template #after>
      <q-item-section
        side
        @click.stop
      >
        <cancel-button
          flat
          @click="$emit('remove')"
        />
      </q-item-section>
    </template>

    <content-dialog
      v-model="dialogOpen"
      title="Edit Line Item"
      close-icon="mdi-arrow-left"
    >
      <q-list padding dense>
        <q-item-wrapper
          label="Description"
          label-class="text-weight-medium"
          required
        >
          <InventoryInput
            :model-value="row.description"
            type="textarea"
            placeholder="Enter description or search inventory..."
            dense
            stack-label
            outlined
            :selected="row.item_id"
            :error="!!errors['description']"
            :error-message="!!errors['description'] ? errors['description'][0] : null"
            @update:model-value="(e) => row.description = e"
            @select="onSelectInventoryItem"
          />
        </q-item-wrapper>

        <div class="row">
          <div class="col-6">
            <q-item-wrapper
              label="Qty / Hrs"
              label-class="text-weight-medium"
            >
              <q-input
                :model-value="row.quantity"
                :error="!!errors['quantity']"
                :error-message="!!errors['quantity'] ? errors['quantity'][0] : null"
                type="number"
                min="0"
                max="9999"
                dense
                outlined
                style="max-width: 100%;"
                @update:model-value="onQuantityInput"
              />
            </q-item-wrapper>
          </div>

          <div class="col-6">
            <q-item-wrapper
              label="Unit Price"
              label-class="text-weight-medium"
            >
              <q-input
                prefix="$"
                :model-value="row.unit_price"
                :error="!!errors['unit_price']"
                :error-message="!!errors['unit_price'] ? errors['unit_price'][0] : null"
                type="number"
                min="0"
                max="99999"
                dense
                outlined
                style="max-width: 100%;"
                @update:model-value="onUnitPriceInput"
              />
            </q-item-wrapper>
          </div>
        </div>

        <q-item-wrapper
          label="Discount"
          label-class="text-weight-medium"
        >
          <q-input
            :model-value="row.discount_rate"
            :error="!!errors['discount_rate']"
            :error-message="!!errors['discount_rate'] ? errors['discount_rate'][0] : null"
            type="number"
            min="0"
            max="100"
            dense
            outlined
            suffix="%"
            @update:model-value="onDiscountInput"
          />
        </q-item-wrapper>

        <q-item-wrapper
          v-if="showTaxTypeOptions"
          label="GST"
          label-class="text-weight-medium"
        >
          <q-select
            v-model="row.tax_type"
            :options="taxTypeOptions"
            emit-value
            map-options
            dense
            outlined
            dropdown-icon="mdi-chevron-down"
            class="q-mb-sm"
          />
        </q-item-wrapper>

        <q-item-wrapper>
          <SelectedInventoryItem
            :inventory-item="row.inventory_item"
            :tax-type="row.tax_type"
            :description="row.description"
            :unit-price="row.unit_price"
            @remove="onRemoveInventoryItem"
            @create="onSelectInventoryItem"
          />
        </q-item-wrapper>

        <q-separator spaced />

        <q-toolbar>
          <div>
            <q-item-label>
              Line Total
            </q-item-label>
            <q-item-label class="text-h6 text-blue-7">
              {{ linePriceStr }}
            </q-item-label>
          </div>

          <q-space />

          <q-btn
            v-close-popup
            label="Delete Line"
            color="red-7"
            @click="$emit('remove')"
          />
        </q-toolbar>
      </q-list>
    </content-dialog>
  </q-item-wrapper>
</template>

<script>
import SelectedInventoryItem from 'components/app/accounting/documents/containers/SelectedInventoryItem'
import InventoryInput from 'components/app/inventory/partials/InventoryInput'
import calculationMixin, {
  convertUnitPriceForTaxType,
  getTaxRate,
  toDisplayableCurrency,
  dineroFromFloat,
  floatFromDinero
} from 'components/app/accounting/documents/calculationMixin'
import { toDecimal } from 'dinero.js'

export default {
  components: {
    InventoryInput, SelectedInventoryItem
  },
  mixins: [calculationMixin],
  props: {
    row: {
      type: Object,
      required: true
    },
    errors: {
      type: Object,
      default: () => {
        return {}
      }
    }
  },
  emits: ['remove'],
  data () {
    return {
      dialogOpen: false,
      taxTypeOptions: [
        { label: 'GST not applicable', value: 'NONE' },
        { label: 'Line includes GST (Inclusive)', value: 'INCLUSIVE' },
        { label: 'Line excludes GST (Exclusive)', value: 'EXCLUSIVE' }
      ]
    }
  },
  computed: {
    showTaxTypeOptions () {
      return this.row.tax_type !== 'NONE' || this.company.default_gst !== 'NONE'
    },
    lineCalculations () {
      return this.calculateLineTotals(this.row, this.row.tax_type)
    },
    linePriceStr () {
      let str = toDisplayableCurrency(this.lineCalculations.totalBeforeTaxIncDiscount)
      if (parseFloat(toDecimal(this.lineCalculations.totalBeforeTaxIncDiscount)) > 0 && this.row.with_tax !== 'NONE') {
        str += ` ${this.row.tax_type === 'INCLUSIVE' ? 'inc.' : 'plus'} GST`
      }
      return str
    }
  },
  methods: {
    onSelectInventoryItem (item) {
      this.row.inventory_item = item
      this.row.item_id = item.id
      this.row.description = item.prefill_column === 'DESCRIPTION' && item.description ? item.description : item.name
      this.row.tax_type = item.selling_tax_type
      this.row.unit_price = convertUnitPriceForTaxType(
        this.row.tax_type,
        item.selling_tax_type,
        item.selling_unit_price,
        getTaxRate(null, this.row.tax_type)
      )
    },
    onRemoveInventoryItem () {
      this.row.inventory_item = null
      this.row.item_id = null
    },
    onQuantityInput (val) {
      if (isNaN(val) || !val || val < 0) {
        this.row.quantity = 1
      }
      else {
        this.row.quantity = floatFromDinero(dineroFromFloat(val))
      }
    },
    onUnitPriceInput (val) {
      val = parseFloat(val)
      if (isNaN(val) || !val || val < 0) {
        this.row.unit_price = 0
      }
      else {
        // Truncate to 2 decimal places
        this.row.unit_price = floatFromDinero(dineroFromFloat(val))
      }
    },
    onDiscountInput (val) {
      val = parseFloat(val)
      if (isNaN(val) || !val || val < 0 || val > 100) {
        this.row.discount_rate = 0
      }
      else {
        // Truncate to 2 decimal places
        this.row.discount_rate = floatFromDinero(dineroFromFloat(val))
      }
    }
  }
}
</script>
